import { useCallback, useMemo, useState } from 'react'
import { Box, Flex } from '@mantine/core'
import { useIsMutating } from '@tanstack/react-query'

import { AccountItem } from '@/modules/accounts/components/AccountItem/AccountItem'
import {
  useAccountItemDelete,
  useManualAccountItemDelete,
} from '@/modules/accounts/components/AccountsList/AccountList.config'
import type { AccountsListProps } from '@/modules/accounts/components/AccountsList/AccountsList.types'

import { ConfirmationModal } from '@/components/ConfirmationModal/ConfirmationModal'
import { ManualAccountEdit } from '@/components/ManualAccountEdit/ManualAccountEdit'
import { ManualAccountModal } from '@/components/ManualAccountModal/ManualAccountModal'

import classes from './AccountsList.module.css'

export function AccountsList({ accounts }: AccountsListProps) {
  const [openedDelete, setOpenedDelete] = useState<boolean>(false)
  const [openedEdit, setOpenedEdit] = useState<boolean>(false)
  const [currentAccountId, setCurrentAccountId] = useState<string>('')

  const { deleteAccount } = useAccountItemDelete(currentAccountId)
  const { deleteManualAccount } = useManualAccountItemDelete(currentAccountId)

  const isMutating = useIsMutating({
    exact: true,
  })

  const findAccount = useCallback(
    (id: string) => accounts.find(account => account.plaidId === id),
    [accounts],
  )

  const currentAccount = useMemo(
    () => findAccount(currentAccountId ?? ''),
    [currentAccountId, findAccount],
  )

  const handleDeleteAccount = () => {
    if (!currentAccountId) return

    if (currentAccount?.isManual) {
      deleteManualAccount()
    } else {
      deleteAccount()
    }
  }

  const handleOpenDeleteModal = (id: string | undefined) => {
    if (!id) return

    setCurrentAccountId(id)
    setOpenedDelete(true)
  }

  const handleOpenEditModal = (id: string | undefined) => {
    if (!id) return

    setCurrentAccountId(id)
    setOpenedEdit(true)
  }

  return (
    <>
      <Box className={classes.rightColumn}>
        <Flex className={classes.accountsList}>
          {accounts.map(account => (
            <AccountItem
              key={account.plaidId}
              account={account}
              openDeleteModal={handleOpenDeleteModal}
              openEditModal={handleOpenEditModal}
            />
          ))}
        </Flex>
      </Box>

      <ConfirmationModal
        button={{ label: 'Delete', variant: 'error' }}
        description={`Are you sure you want to delete ${currentAccount?.mask ? `**** **** **** ${currentAccount.mask}` : ``} ${currentAccount?.name}?`}
        isLoading={!!isMutating}
        onClose={() => setOpenedDelete(false)}
        onConfirm={handleDeleteAccount}
        opened={openedDelete}
        title="Delete account"
      />

      <ManualAccountModal
        onClose={() => setOpenedEdit(false)}
        opened={openedEdit}
      >
        <ManualAccountEdit
          accountId={currentAccountId}
          onClose={() => setOpenedEdit(false)}
        />
      </ManualAccountModal>
    </>
  )
}
